import React, { useCallback, useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Skeleton,
  Alert,
  Stack,
  IconButton,
} from '@mui/material';
import { History, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useListaPermitida } from '../../../authorization/useListaPermitida';
import { employeeService, type WorkHistoryEvent } from '../../../services/employeeService';
import { rotuloDoEvento } from './employeeWorkHistoryLabels';

interface EmployeeCardProps {
  employeeId: number;
}

const ACCENT = '#1565C0';
const PAGE_SIZE = 5;
const MENSAGEM_DE_ERRO = 'Não conseguimos carregar o histórico agora. Tente de novo em alguns minutos.';

const formatDate = (d: string) => {
  try { return new Date(d + 'T00:00:00').toLocaleDateString('pt-BR'); } catch { return d; }
};

export const EmployeeWorkHistoryCard: React.FC<EmployeeCardProps> = ({ employeeId }) => {
  const [page, setPage] = useState(0);

  const carregar = useCallback(() => employeeService.getWorkHistory(employeeId), [employeeId]);
  const lista = useListaPermitida<WorkHistoryEvent>('employee.read', 'Histórico de trabalho', carregar);
  const events = lista.itens;
  const loading = lista.carregando;

  const trocasDeSetor = events.filter((e) => e.eventType === 'MUDANCA_SETOR').length;
  const trocasDeCargo = events.filter((e) => e.eventType === 'MUDANCA_CARGO').length;

  const totalPages = Math.ceil(events.length / PAGE_SIZE);
  const paged = events.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <Card variant="outlined" sx={{ height: '100%', borderTop: `3px solid ${ACCENT}` }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Box sx={{ color: ACCENT, display: 'flex' }}><History fontSize="small" /></Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
            Histórico de Trabalho
          </Typography>
          {!loading && !lista.erro && (
            <Chip label={events.length} size="small" sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 700 }} />
          )}
        </Box>

        {loading && (
          <Stack spacing={1}>
            {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={32} />)}
          </Stack>
        )}

        {lista.erro && (
          <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>{MENSAGEM_DE_ERRO}</Alert>
        )}

        {!loading && !lista.erro && events.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {lista.bloqueio ?? 'Nenhum registro encontrado'}
          </Typography>
        )}

        {!loading && !lista.erro && events.length > 0 && (
          <>
            {(trocasDeSetor > 0 || trocasDeCargo > 0) && (
              <Box display="flex" gap={0.5} flexWrap="wrap" mb={1.5}>
                {trocasDeSetor > 0 && (
                  <Chip label={`${trocasDeSetor} troca${trocasDeSetor !== 1 ? 's' : ''} de setor`} size="small" variant="outlined" />
                )}
                {trocasDeCargo > 0 && (
                  <Chip label={`${trocasDeCargo} troca${trocasDeCargo !== 1 ? 's' : ''} de cargo`} size="small" variant="outlined" />
                )}
              </Box>
            )}

            <Stack spacing={0.75}>
              {paged.map((e) => (
                <Box
                  key={e.id}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{ py: 0.5, px: 1, borderRadius: 1, bgcolor: 'action.hover', '@media (max-width:520px)': { flexWrap: 'wrap' } }}
                >
                  <Typography variant="caption" sx={{ minWidth: 72 }}>
                    {formatDate(e.eventDate)}
                  </Typography>
                  <Chip
                    label={rotuloDoEvento(e.eventType)}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    data-testid="work-history-notes"
                    sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', '@media (max-width:520px)': { flexBasis: '100%' } }}
                  >
                    {e.notes ?? ''}
                  </Typography>
                </Box>
              ))}
            </Stack>

            {totalPages > 1 && (
              <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5} mt={1}>
                <Typography variant="caption" color="text.secondary">
                  {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, events.length)} de {events.length}
                </Typography>
                <IconButton size="small" aria-label="Página anterior" disabled={page === 0} onClick={() => setPage(page - 1)}>
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <IconButton size="small" aria-label="Próxima página" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
